import React, { ReactNode, useCallback, useState } from 'react';

/**
 * Shape of the per-tenant RTK Query hooks the master pages use
 * (`useGetMasterBookingsQuery`, `useGetMasterStationsQuery`, …). Only the
 * fields the fan-out needs are required.
 */
type TenantQueryHook<T> = (arg: { tenant: string }) => {
    data?: T[];
    isError: boolean;
    isSuccess: boolean;
    isFetching: boolean;
};

interface TenantQuerySlotProps<T> {
    tenant: string;
    useQuery: TenantQueryHook<T>;
    onResult: (tenant: string, rows: T[]) => void;
    onError: (tenant: string) => void;
    onLoaded: (tenant: string) => void;
    onFetching: (tenant: string) => void;
}

/**
 * One per-tenant query slot. RTK Query hooks can't be called in a loop
 * (hook order must be stable), so we render one of these per tenant slug
 * to get N concurrent queries with stable hook order, reporting results
 * back via callbacks.
 */
function TenantQuerySlot<T>({
    tenant,
    useQuery,
    onResult,
    onError,
    onLoaded,
    onFetching,
}: TenantQuerySlotProps<T>): null {
    const { data, isError, isSuccess, isFetching } = useQuery({ tenant });

    React.useEffect(() => {
        // Also fires on a reload (cache invalidation): un-marking the
        // tenant as loaded makes the shared "loading x of y tenants"
        // progress line reappear while the refetch runs.
        if (isFetching) {
            onFetching(tenant);
            return;
        }
        if (isSuccess) {
            onResult(tenant, data ?? []);
            onLoaded(tenant);
        } else if (isError) {
            onError(tenant);
            onLoaded(tenant);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenant, isSuccess, isError, isFetching, data]);

    return null;
}

export interface MasterTenantData<T> {
    rowsByTenant: Record<string, T[]>;
    failedTenants: Set<string>;
    loadedTenants: Set<string>;
    /** The per-tenant query fan-out to render somewhere in the page. */
    querySlots: ReactNode;
}

/**
 * Fans `useQuery` out across `accessibleTenants` and aggregates the
 * results. Shared by the three master list pages, which differ only in
 * their row type and the query hook they pass in.
 */
export function useMasterTenantData<T>(
    accessibleTenants: string[],
    useQuery: TenantQueryHook<T>,
): MasterTenantData<T> {
    const [rowsByTenant, setRowsByTenant] = useState<Record<string, T[]>>({});
    const [failedTenants, setFailedTenants] = useState<Set<string>>(new Set());
    const [loadedTenants, setLoadedTenants] = useState<Set<string>>(new Set());

    const handleResult = useCallback((tenant: string, rows: T[]) => {
        setRowsByTenant((prev) => ({ ...prev, [tenant]: rows }));
        setFailedTenants((prev) => {
            if (!prev.has(tenant)) return prev;
            const next = new Set(prev);
            next.delete(tenant);
            return next;
        });
    }, []);

    const handleError = useCallback((tenant: string) => {
        setFailedTenants((prev) => {
            if (prev.has(tenant)) return prev;
            const next = new Set(prev);
            next.add(tenant);
            return next;
        });
    }, []);

    const handleLoaded = useCallback((tenant: string) => {
        setLoadedTenants((prev) => {
            if (prev.has(tenant)) return prev;
            const next = new Set(prev);
            next.add(tenant);
            return next;
        });
    }, []);

    const handleFetching = useCallback((tenant: string) => {
        setLoadedTenants((prev) => {
            if (!prev.has(tenant)) return prev;
            const next = new Set(prev);
            next.delete(tenant);
            return next;
        });
    }, []);

    const querySlots = accessibleTenants.map((tenant) => (
        <TenantQuerySlot
            key={tenant}
            tenant={tenant}
            useQuery={useQuery}
            onResult={handleResult}
            onError={handleError}
            onLoaded={handleLoaded}
            onFetching={handleFetching}
        />
    ));

    return { rowsByTenant, failedTenants, loadedTenants, querySlots };
}
